import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import AvatarSkeleton from '../../../../avatar/AvatarSkeleton'
import Skeleton from '@mui/material/Skeleton'
import UploadImage from '../../../../FormControl/input/UploadImage'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import InputField from '../../../../FormControl/input/InputField'
import SelectTimezone from '../../../../commom/FormControl/CustomSelect/SelectTimezone'
import { handlerErrorV2 } from '../../../../../utils/errors'
import { validate } from '../../../../../utils'
import isEmpty from 'lodash/isEmpty'
import { updateTenant, updateTenantLogo } from '../../../../../services/tenant/tenant.service'
import { updateTenantBasicInformation } from '../../../../../store/actions/tenant.actions'
import { useSnackbar } from 'notistack'
import { deleteFiles, presigneUrl, uploadFile } from '../../../../../services/upload/upload.service'
import globalConfig from '../../../../../config/globalConfig'
import { useDispatch, useSelector } from 'react-redux'
import { FormControlLabel, Switch, Tooltip } from '@mui/material'
import SelectField from '../../../../FormControl/input/SelectField'

const SkeletonBasicInformation = () => {
  return (
    <Grid container justifyContent='space-between'>
      <Grid item lg={2} sm={3} className='flex justify-end'>
        <AvatarSkeleton />
      </Grid>
      <Grid item lg={10} sm={9} container spacing={2} alignItems={'center'}>
        <Grid item md={2} sm={3}>
          <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
            Name
          </Typography>
        </Grid>
        <Grid item md={10} sm={9}>
          <Typography>
            <Skeleton />
          </Typography>
        </Grid>
        <Grid item md={2} sm={3}>
          <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
            Address
          </Typography>
        </Grid>
        <Grid item md={10} sm={9}>
          <Typography>
            <Skeleton />
          </Typography>
        </Grid>
        <Grid item md={2} sm={3}>
          <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
            Country
          </Typography>
        </Grid>
        <Grid item md={4}>
          <Typography>
            <Skeleton />
          </Typography>
        </Grid>
        <Grid item md={2} sm={3}>
          <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
            State
          </Typography>
        </Grid>
        <Grid item md={4}>
          <Typography>
            <Skeleton />
          </Typography>
        </Grid>
        <Grid item md={2} sm={3}>
          <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
            Timezone
          </Typography>
        </Grid>
        <Grid item md={10} sm={9}>
          <Typography>
            <Skeleton />
          </Typography>
        </Grid>
        <Grid item md={2} sm={3}>
          <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
            Domain
          </Typography>
        </Grid>
        <Grid item md={10} sm={9}>
          <Typography>
            <Skeleton />
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  )
}

const optionsCountries = [{ value: 'AU', label: 'Australia' }]

const optionsStates = [
  { value: 'VIC', label: 'Victoria' },
  { value: 'NSW-Eastern', label: 'New South Wales (Eastern Division)' },
  { value: 'SA', label: 'South Australia' },
  { value: 'WA', label: 'Western Australia' }
]

const BasicInformation = ({ permission, tenant = {}, isLoading }) => {
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const sessionUserRoles = useSelector((state) => state.session?.user?.roles?.map((item) => item.id) || [])
  const [form, setInputForm] = useState(tenant || {})
  const [error, setInputErrors] = useState({})
  const [loading, toggleLoading] = useState(false)

  const onChange = (name, value) => {
    setInputForm({ ...form, [name]: value })
    setInputErrors({ ...error, [name]: '' })
  }

  useEffect(() => {
    setInputForm(tenant || {})
  }, [tenant])

  const onSave = async (e) => {
    e.preventDefault()
    const fieldRules = {
      name: 'required',
      address: 'required',
      timezone: 'required'
    }
    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      setInputErrors(errors)
      return
    }
    try {
      toggleLoading(true)
      const result = await updateTenant(form)
      toggleLoading(false)
      if (!result.data && result.error) {
        setInputErrors({ message: 'Something went wrong. Please try again.' })
      } else {
        dispatch(updateTenantBasicInformation(result.data))
        enqueueSnackbar('Updated successfully', { variant: 'success' })
      }
    } catch (error) {
      toggleLoading(false)
      const message = handlerErrorV2(error)
      setInputErrors({ message })
    }
  }

  const onUpload = async (files) => {
    try {
      const logo = tenant.logo
      const resultDelete = await deleteFiles([{ name: logo }], 'public')
      if (resultDelete.error) {
        return { error: resultDelete.error.message }
      }

      const resultUpload = await presigneUrl(files, 'public')
      if (resultUpload.error || !resultUpload.data) {
        return { error: resultUpload.error.message }
      } else {
        const result = await Promise.all(
          resultUpload.data.map(async (file) => {
            const { data } = file
            if (!data) {
              return null
            }
            await uploadFile(data.presignedUrl, files[0]?.file)
            return data.filename
          })
        )

        if (result) {
          const response = await updateTenantLogo({ logo: result[0] })
          if (!response.data || response.error) {
            return { error: response.error.message }
          } else {
            enqueueSnackbar('Updated successfully', { variant: 'success' })
            return { error: null }
          }
        }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: message }
    }
  }

  return (
    <>
      <Box className='bg-primary text-primary-foreground px-4 py-1 flex gap-4 items-center justify-between h-8'>
        <Typography variant='subtitle1' className='uppercase'>
          Basic information
        </Typography>
        {!isLoading && permission && (
          <ButtonStartIcon
            onClick={onSave}
            color='secondary'
            variant='contained'
            startIcon={['fal', 'check']}
            loading={loading}
          >
            Save
          </ButtonStartIcon>
        )}
      </Box>
      <Box className='px-2 pt-4 pb-4'>
        {isLoading ? (
          <SkeletonBasicInformation />
        ) : (
          <Grid container justifyContent='space-between'>
            <Grid item lg={2} sm={3} className='flex justify-end'>
              <UploadImage
                value={`${globalConfig.storage}${form?.logoUrl}`}
                onChange={(value) => onChange('media', value)}
                onUpload={onUpload}
                permission={permission}
                filename='logo_institute.png'
              />
            </Grid>
            <Grid item lg={10} sm={9} container spacing={2} alignItems={'center'}>
              <Grid item md={2} sm={3} className='flex justify-end'>
                <FormControlLabel
                  name='hideName'
                  control={
                    <Tooltip title='Hiden school name in header' arrow placement='top'>
                      <Switch
                        checked={form.hideName}
                        error={error.hideName}
                        helperText={error.hideName}
                        disabled={loading}
                        size='small'
                        onChange={(e) => (permission ? onChange(e.target.name, e.target.checked) : undefined)}
                      />
                    </Tooltip>
                  }
                  style={{ marginRight: 0 }}
                  label={
                    <Typography variant='inherit' color='primary' className='font-bold' align='right'>
                      Name
                    </Typography>
                  }
                />
              </Grid>
              <Grid item md={10} sm={9}>
                {permission ? (
                  <InputField
                    id='tenat-name'
                    name='name'
                    value={form.name}
                    error={error.name}
                    helperText={error.name}
                    disabled={loading}
                    onChange={(e) => onChange(e.target.name, e.target.value)}
                  />
                ) : (
                  <Typography>{tenant?.name}</Typography>
                )}
              </Grid>
              <Grid item md={2} sm={3} className='flex justify-end'>
                <FormControlLabel
                  name='hideAddress'
                  control={
                    <Tooltip title='Hide school address in header' arrow placement='top'>
                      <Switch
                        checked={form.hideAddress}
                        error={error.hideAddress}
                        helperText={error.hideAddress}
                        disabled={loading}
                        size='small'
                        onChange={(e) => (permission ? onChange(e.target.name, e.target.checked) : undefined)}
                      />
                    </Tooltip>
                  }
                  style={{ marginRight: 0 }}
                  label={
                    <Typography variant='inherit' color='primary' className='font-bold' align='right'>
                      Address
                    </Typography>
                  }
                />
              </Grid>
              <Grid item md={10} sm={9}>
                {permission ? (
                  <InputField
                    id='tenant-address'
                    name='address'
                    value={form.address}
                    error={error.address}
                    helperText={error.address}
                    disabled={loading}
                    onChange={(e) => onChange(e.target.name, e.target.value)}
                  />
                ) : (
                  <Typography>{tenant?.address}</Typography>
                )}
              </Grid>
              <Grid item md={2} sm={3}>
                <Typography
                  variant='inherit'
                  color='primary'
                  className='font-bold'
                  align='right'
                  component='p'
                >
                  Country
                </Typography>
              </Grid>
              <Grid item md={4}>
                {permission ? (
                  <SelectField
                    id='tenant-country'
                    name='country'
                    value={form.country}
                    error={error.country}
                    helperText={error.country}
                    disabled={loading}
                    options={optionsCountries}
                    onChange={(e) => onChange(e.target.name, e.target.value)}
                  />
                ) : (
                  <Typography>{tenant?.country}</Typography>
                )}
              </Grid>
              <Grid item md={2} sm={3}>
                <Typography
                  variant='inherit'
                  color='primary'
                  className='font-bold'
                  align='right'
                  component='p'
                >
                  State
                </Typography>
              </Grid>
              <Grid item md={4}>
                {permission ? (
                  <SelectField
                    id='tenant-state'
                    name='state'
                    value={form.state}
                    error={error.state}
                    helperText={error.state}
                    disabled={loading}
                    options={optionsStates}
                    onChange={(e) => onChange(e.target.name, e.target.value)}
                  />
                ) : (
                  <Typography>{tenant?.state}</Typography>
                )}
              </Grid>
              {sessionUserRoles.includes('Maestro') && (
                <>
                  <Grid item md={2} sm={3}>
                    <Typography
                      variant='inherit'
                      color='primary'
                      className='font-bold'
                      align='right'
                      component='p'
                    >
                      School ID
                    </Typography>
                  </Grid>
                  <Grid item md={10} sm={9}>
                    {permission ? (
                      <InputField
                        id='tenant-school-id'
                        name='schoolId'
                        value={form.schoolId}
                        error={error.schoolId}
                        helperText={error.schoolId}
                        disabled={loading}
                        onChange={(e) => onChange(e.target.name, e.target.value)}
                      />
                    ) : (
                      <Typography>{tenant?.schoolId}</Typography>
                    )}
                  </Grid>
                </>
              )}
              <Grid item md={2} sm={3}>
                <Typography
                  variant='inherit'
                  color='primary'
                  className='font-bold'
                  align='right'
                  component='p'
                >
                  Timezone
                </Typography>
              </Grid>
              <Grid item md={10} sm={9}>
                <SelectTimezone
                  id='tenant-timezone'
                  name='timezone'
                  value={form.timezone}
                  error={error.timezone}
                  helperText={error.timezone}
                  permission={permission}
                  disabled={loading}
                  onChange={(e) => onChange(e.target.name, e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  )
}

export default BasicInformation
